import { useSuggestions } from 'utils/hooks';

import {
  Button,
  Checkbox,
  Flex,
  Icon,
  TextField,
  Typography,
} from '@project44/manifest-ds';
import { Skeleton } from '@project44/shared-components';
import { useTranslation } from '@project44/translations-utils';

interface IColumnAddedPanel {
  addedColumns: string[];
  onSelectKey: (refKey: string) => (isSelected?: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

function ColumnAddedPanel({
  addedColumns,
  onSelectKey,
  onCancel,
  onConfirm,
}: IColumnAddedPanel) {
  const { t } = useTranslation();
  const { setQuery, suggestions, isLoading } = useSuggestions(
    'filter.suggestions.attributeKey',
    'filter.suggestions.attributeKey',
  );

  return (
    <Flex orientation="vertical" css={{ height: '100%' }}>
      <Flex
        orientation="vertical"
        gap="medium"
        css={{ flex: 1, overflow: 'scroll', padding: '$large' }}
      >
        <Typography variant="body" css={{ color: '$text-tertiary' }}>
          {t('core.columnAddedPanelDescription')}
        </Typography>
        <TextField
          aria-label={t('core.searchEntity', {
            entity: t('core.referenceKeys'),
          })}
          onChange={(query) => setQuery(query)}
          placeholder={t('core.searchEntity', {
            entity: t('core.referenceKeys'),
          })}
          startIcon={<Icon icon="search" />}
        />
        <Flex orientation="vertical" gap="small">
          {isLoading
            ? [1, 2, 3].map((idx) => (
                <Skeleton key={idx} css={{ height: '24px' }} />
              ))
            : suggestions.map((opt) => (
                <Flex
                  key={opt}
                  justify="between"
                  align="center"
                  css={{ height: '40px' }}
                >
                  {opt}
                  <Checkbox
                    aria-label={t(
                      addedColumns.includes(opt)
                        ? 'core.disableColumn'
                        : 'core.enableColumn',
                      { columnName: opt },
                    )}
                    isSelected={addedColumns.includes(opt)}
                    onChange={onSelectKey(opt)}
                  />
                </Flex>
              ))}
        </Flex>
      </Flex>
      <Flex
        align="center"
        justify="end"
        gap="medium"
        css={{ borderTop: '1px solid $border-primary', padding: '$medium' }}
      >
        <Button
          css={{ color: '$palette-indigo-600' }}
          onPress={onCancel}
          size="medium"
          variant="secondary"
        >
          {t('global.cancel')}
        </Button>
        <Button size="medium" onPress={onConfirm}>
          {t('global.confirm')}
        </Button>
      </Flex>
    </Flex>
  );
}

export default ColumnAddedPanel;
